import { Button, message, Modal, Tooltip } from "antd";
import CIQDatePicker from "components/custom-date-picker";
import DatePickerIcon from "components/svg-icons/datepicker-icon";
import GrayDatePickerIcon from "components/svg-icons/gray-datepicker-icon";
import { ProjectContext } from "context/ProjectProvider";
import { useCIQMutation } from "hooks/ciq-gql-hooks";
import { Moment } from "moment";
import { useCallback, useContext, useMemo, useState } from "react";
import { DATE_FORMAT_MMDDYYYY, DateUtils } from "utils/dateutils";

import "./feature-status.scss";
import { ErrorMessages, EWorkflowStatusDataBlock } from "constants/index";
import { FeatureTypes } from "entity-app/constants";
import { APPLY_WORKFLOW_MUTATION } from "entity-app/shared-components/entity-dateblock/queries/mutation";

type TMilestone = {
  id: string;
  sequence_no: number;
  name: string;
  actual: null | string;
  actual_date: null | string;
};

export default function FeatureLogSetStatusUI(props: {
  refetchLogData?: Function;
  data: {
    id: string;
    auto_inc_value: number;
    sequence_id: number;
    title: string;
    milestones: Array<TMilestone> | undefined;
    date_block: { milestones: Array<TMilestone>; id: string } | undefined;
    workflow_status: EWorkflowStatusDataBlock;
    linked_items?: Array<{
      workflow_status: EWorkflowStatusDataBlock;
      feature_type: FeatureTypes;
    }>;
    feature_workflow_instance_id?: string;
    are_predecessors_complete?: boolean;
  };
  updatePermitted?: boolean;
}) {
  const { data, refetchLogData, updatePermitted = true } = props;
  const sequenceId = data.auto_inc_value || data.sequence_id;
  const headerTitle = `${sequenceId}-${data.title}`;

  const [visible, setVisible] = useState(false);
  const [selectedDate, setSelectedDate] = useState(DateUtils.dateTimeObj());
  const { gqlClientForProject } = useContext(ProjectContext);

  const milestoneInfo = useMemo(() => {
    const milestones = data.milestones || data.date_block?.milestones || [];

    const sortedMilestones = milestones
      .slice()
      .sort((a, b) => a.sequence_no - b.sequence_no);
    const nextMilestone = sortedMilestones.find(
      (x) => !x.actual_date && !x.actual
    );

    let currentMilestone: TMilestone | undefined;

    if (nextMilestone && nextMilestone.sequence_no > 1) {
      const sequenceNo = nextMilestone.sequence_no - 1;
      currentMilestone = milestones.find((x) => x.sequence_no === sequenceNo);
    }

    return { currentMilestone, nextMilestone };
  }, [data?.date_block?.milestones, data.milestones]);

  const [applyWorkflowMutation, { loading }] = useCIQMutation(
    APPLY_WORKFLOW_MUTATION,
    {
      client: gqlClientForProject
    }
  );

  const disabledActualDate = useCallback(
    (current: Moment) => {
      const after = DateUtils.dateTimeObj(current).isAfter(
        DateUtils.dateTimeObj()
      );

      const currentActualDate =
        milestoneInfo.currentMilestone?.actual_date ||
        milestoneInfo.currentMilestone?.actual;

      if (!currentActualDate) return after;

      const currDateFromPicker = DateUtils.dateTimeObj(current)
        .hours(13)
        .minutes(0)
        .seconds(0);
      const before = currDateFromPicker.isBefore(
        DateUtils.dateTimeObj(currentActualDate)
      );
      return before || after;
    },
    [milestoneInfo.currentMilestone]
  );

  const isEntityWorkflowCompleted = useMemo(() => {
    return !milestoneInfo.nextMilestone;
  }, [milestoneInfo.nextMilestone]);

  const linkedSubmittalsWfCompleted = useMemo(() => {
    if (data?.workflow_status === EWorkflowStatusDataBlock.WF_NOT_STARTED) {
      const areAnySubmittalsInProgress = data?.linked_items?.some(
        (x) =>
          x.feature_type === FeatureTypes.SUBMITTALS &&
          (x.workflow_status === EWorkflowStatusDataBlock.WF_NOT_STARTED ||
            x.workflow_status === EWorkflowStatusDataBlock.IN_PROGRESS)
      );

      return !areAnySubmittalsInProgress;
    }
    return true;
  }, [data?.linked_items, data?.workflow_status]);

  const onConfirm = async () => {
    const saveDate = DateUtils.formatDateWithLunchTime(selectedDate);

    const variables = {
      input: [
        {
          feature_instance_id: data?.id,
          workflow_instance_id:
            data.feature_workflow_instance_id || data?.date_block?.id,
          milestones: [
            { actual_date: saveDate, id: milestoneInfo.nextMilestone?.id }
          ]
        }
      ],
      metadata: {
        request_source: "INLINE_EDIT_LOG_PAGE"
      }
    };

    const updateResponse = await applyWorkflowMutation({ variables });

    if (updateResponse.success) {
      message.success("Updated successfully.");
      setVisible(false);
      if (refetchLogData) setTimeout(refetchLogData, 1000);
    } else {
      message.error("Failed to update.");
    }
  };

  const earlyReleasedMsg =
    "Linked predecessors have not been completed. To release the Material ahead of schedule, go to Material Details.";

  const tooltipTitle = useMemo(() => {
    if (!updatePermitted) {
      return ErrorMessages.PermissionNotGranted;
    }

    if (
      linkedSubmittalsWfCompleted === false ||
      (data?.are_predecessors_complete === false &&
        data?.workflow_status === EWorkflowStatusDataBlock.WF_NOT_STARTED)
    ) {
      return earlyReleasedMsg;
    }
    if (isEntityWorkflowCompleted === true) {
      return "Workflow completed";
    }

    return "Update status";
  }, [
    updatePermitted,
    linkedSubmittalsWfCompleted,
    isEntityWorkflowCompleted,
    earlyReleasedMsg,
    data?.are_predecessors_complete,
    data?.workflow_status
  ]);

  return (
    <div className="flex ">
      <Tooltip title={tooltipTitle}>
        {!updatePermitted ||
        isEntityWorkflowCompleted === true ||
        (data?.are_predecessors_complete === false &&
          data?.workflow_status === EWorkflowStatusDataBlock.WF_NOT_STARTED) ||
        linkedSubmittalsWfCompleted === false ? (
          <div className="flex w-[46px] justify-center">
            <GrayDatePickerIcon />
          </div>
        ) : (
          <Button
            className="flex space-x-2 items-center border-none bg-transparent disabled:bg-slate-600"
            onClick={() => setVisible(true)}
            data-testid="featureinstancestatus-button-datepicker"
          >
            <DatePickerIcon />
          </Button>
        )}
      </Tooltip>
      <Modal
        title={
          <div
            className="px-3"
            data-testid="featureinstancestatus-modal"
          >{`Update Status of ${headerTitle ?? ""}`}</div>
        }
        bodyStyle={{ height: 330 }}
        open={visible}
        destroyOnClose
        onOk={onConfirm}
        okText="Confirm"
        okButtonProps={{ disabled: loading, loading }}
        cancelButtonProps={{ disabled: loading }}
        onCancel={() => {
          setVisible(false);
        }}
        width={391}
      >
        <div className="flex flex-col feature-status-popover-content justify-center space-y-1">
          <div className=" text-xs w-full text-left">
            <span className="font-semibold uppercase">
              {milestoneInfo.nextMilestone?.name}
            </span>{" "}
            on {DateUtils.format(selectedDate, DATE_FORMAT_MMDDYYYY)}
          </div>
          <CIQDatePicker
            value={selectedDate}
            className="w-full"
            onChange={(val: any) => {
              setSelectedDate(val);
            }}
            open
            getPopupContainer={(trigger: any) => {
              return trigger;
            }}
            showToday={false}
            disabledDate={disabledActualDate}
          />
        </div>
      </Modal>
    </div>
  );
}
